import React from 'react';
import PropTypes from 'prop-types';
import { NavLink, matchPath } from 'react-router-dom';
import { styled } from '@linaria/react';
import { COLORS, Pill, DirectionalIcon } from '@livecommerce/design-system';

import useProductDataOutsideRoutes from 'hooks/useProductDataOutsideRoutes.js';
import { useLocalization } from 'contexts/LocalizationContext.jsx';
import { trackingService } from 'services/tracking/TrackingService.js';
import { buildRoute } from 'constants/routes.js';
import { useNavigationFrameContext } from 'contexts/NavigationFrameContext.jsx';
import { Flair } from 'components-v3/Flairs/Flair.jsx';
import { useDelegatedFrameContext } from 'contexts/DelegatedFrameContext.jsx';
import UpsellIndicator from 'components-v3/Upsell/UpsellIndicator.jsx';

const StyledIcon = styled(DirectionalIcon)`
  width: 24px;
`;

const StyledLink = styled(NavLink)`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding: 4px;
  border-radius: 8px;
  text-decoration: none;
  color: ${COLORS.white};

  ${StyledIcon} {
    color: transparent;
  }

  &.active {
    ${StyledIcon} {
      color: inherit;
    }
  }

  &.active,
  &:hover {
    background-color: ${COLORS.white};
    color: ${COLORS.dark_700};
  }
`;

const StyledFlair = styled(Flair)`
  display: inline-block;
  vertical-align: bottom;
  height: 12px;
  width: 12px;
`;

const StyledPill = styled(Pill)`
  overflow: hidden;
  display: inline-block;
  text-overflow: ellipsis;
`;

const StyledSpan = styled.span`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

// An entry names the routes it owns beyond its own `to` in `activePaths` --
// a product's detail tab belongs to the listing it shows, not to the bare
// /products listing whose path it shares a prefix with. A shallow path without
// `exact` claims its whole subtree: `/products/:id/view` unqualified would take
// the knowledge and AI Visibility tabs with it. Name the narrowest path that is
// still the entry, and mark it `exact` whenever anything nests underneath.
export const activePathsAreActive = ({ match, location, activePaths = [] }) => {
  if (match) return true;
  if (typeof location?.pathname !== 'string') return false;
  return activePaths.some(({ path, exact = false }) => (
    Boolean(matchPath(location.pathname, { path, exact }))
  ));
};

const SubMenuItem = ({ disabled, subMenuItem, ref, flair }) => {
  const { t } = useLocalization();
  const { setSelectedNavigationItemTitle } = useNavigationFrameContext();
  const {
    title,
    route,
    routeParams,
    queryParams,
    activePaths,
    pills = [],
    exact = false,
    upsellFeatureName,
    dataTestId,
  } = subMenuItem;
  const { selectedProduct, orgId, clientId } = useProductDataOutsideRoutes();
  const { delegatedFrame } = useDelegatedFrameContext();

  const handleSubmenuLinkClick = (e, title, builtRoute, route) => {
    if (disabled) return;

    setSelectedNavigationItemTitle(null);

    trackingService.track({
      eventType: 'sidebar-navigation:submenu',
      destination: title,
      path: builtRoute,
    });

    if (delegatedFrame?.supportsRoute(route)) {
      delegatedFrame.changeRoute(builtRoute);
      e.preventDefault();
      return false;
    }
  };

  const builtRoute = buildRoute(route, { orgId, selectedProduct, clientId, ...routeParams }, queryParams);
  const isActive = activePaths
    ? (match, location) => activePathsAreActive({ match, location, activePaths })
    : undefined;

  return (
    <li key={title} data-test-id={dataTestId || `${title}SidebarSubMenuItem`} ref={ref}>
      <StyledLink
        to={builtRoute}
        onClick={(e) => handleSubmenuLinkClick(e, title, builtRoute, route)}
        exact={exact}
        isActive={isActive}
      >
        <StyledIcon name="arrowRegular" direction="right" />
        <StyledSpan>
          {title}
          {flair && <StyledFlair {...flair} outerColor="transparent" />}
        </StyledSpan>
        {upsellFeatureName && <UpsellIndicator featureName={upsellFeatureName} />}
        {pills.map(pill => {
          return (
            <StyledPill
              variant={pill?.variant}
              backgroundColor={pill?.backgroundColor}
              color={pill?.color}
            >
              {t(pill.label)}
            </StyledPill>
          );
        })}
      </StyledLink>
    </li>
  );
};

SubMenuItem.propTypes = {
  disabled: PropTypes.bool,
  subMenuItem: PropTypes.object,
};

export default React.forwardRef((props, ref) => <SubMenuItem {...props} forwardedRef={ref} />);
